import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared/CollaboratorAvatar';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import { searchCollaboratorsByName } from '@/services/collaboratorSearchService';
import { useDebounce } from '@/hooks/useDebounce';
import { useAllocationManagersQuery } from '../../../../hooks/data/useAllocationManagersQuery';
import * as definitions from './managerCombobox.definitions';
import type { ManagerComboboxProps } from './managerCombobox.types';

const ManagerCombobox: React.FC<ManagerComboboxProps> = ({
  suggestions,
  value,
  onChange,
  disabled,
  placeholder,
  excludeIds,
  className,
  id,
  hasMultipleAllocationManagers,
}) => {
  const [query, setQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<CollaboratorPublic | null>(null);
  const [pickedFromSearch, setPickedFromSearch] = useState<CollaboratorPublic | null>(null);
  const [searchResults, setSearchResults] = useState<CollaboratorPublic[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);
  const { data: allocationManagers = [] } = useAllocationManagersQuery();

  const allowedManagerIds = useMemo(
    () =>
      new Set([
        ...allocationManagers.map((m) => m.id),
        ...suggestions.filter((s) => s.source === 'registry').map((s) => s.collaborator.id),
      ]),
    [allocationManagers, suggestions]
  );

  const selectedFromSuggestions = useMemo(
    () => suggestions.find((s) => s.collaborator.email === value),
    [suggestions, value]
  );

  useEffect(() => {
    if (selectedFromSuggestions) {
      setSelectedItem(selectedFromSuggestions.collaborator);
      setPickedFromSearch(null);
    } else if (pickedFromSearch && pickedFromSearch.email === value) {
      setSelectedItem(pickedFromSearch);
    } else if (value) {
      const fromAllocation = allocationManagers.find((m) => m.email === value);
      if (fromAllocation) {
        setSelectedItem(fromAllocation);
        setPickedFromSearch(null);
      } else {
        setSelectedItem({ email: value, name: value } as any);
        setPickedFromSearch(null);
      }
    } else {
      setSelectedItem(null);
      setPickedFromSearch(null);
    }
  }, [value, selectedFromSuggestions, pickedFromSearch, allocationManagers]);

  useEffect(() => {
    let isMounted = true;

    const doSearch = async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        setSearchResults([]);
        setSearchError(false);
        return;
      }

      setSearchLoading(true);
      setSearchError(false);
      try {
        const results = await searchCollaboratorsByName(debouncedQuery);
        if (isMounted) {
          const normalized: CollaboratorPublic[] = results
            .map((r) => ({
              id: r.collaboratorId,
              name: r.name,
              email: r.email || '',
              status: r.status || '',
              photoUrl: r.avatarUrl,
              profilePath: r.profilePath,
              positionName: r.positionName,
              isRaroAcademyAlumnus: r.isRaroAcademyAlumnus,
            } as unknown as CollaboratorPublic))
            .filter((m) => !excludeIds || !excludeIds.includes(m.id))
            .filter((m) => allowedManagerIds.has(m.id));

          const allocationManagerIds = new Set(allocationManagers.map((m) => m.id));
          const sorted = [
            ...normalized.filter((m) => allocationManagerIds.has(m.id)),
            ...normalized.filter((m) => !allocationManagerIds.has(m.id)),
          ];

          setSearchResults(sorted);
          setSearchError(false);
        }
      } catch (err) {
        console.error('Search failed', err);
        if (isMounted) {
          setSearchError(true);
          setSearchResults([]);
        }
      } finally {
        if (isMounted) setSearchLoading(false);
      }
    };

    doSearch();

    return () => {
      isMounted = false;
    };
  }, [debouncedQuery, allocationManagers, excludeIds, allowedManagerIds]);

  // Filtrar gestores de alocação pelo query
  const filteredAllocationManagers = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      return [];
    }

    const lowerQuery = debouncedQuery.toLowerCase();
    return allocationManagers.filter(
      (manager) =>
        (manager.name?.toLowerCase().includes(lowerQuery) ||
          manager.email?.toLowerCase().includes(lowerQuery)) &&
        (!excludeIds || !excludeIds.includes(manager.id))
    );
  }, [debouncedQuery, allocationManagers, excludeIds]);

  const handleSelectionChange = (collaborator: CollaboratorPublic | null) => {
    if (collaborator) {
      onChange(collaborator.email);
      setQuery('');
      setPickedFromSearch(collaborator);
      inputRef.current?.blur();
    }
  };

  const handleClear = useCallback(() => {
    onChange('');
    setQuery('');
    setPickedFromSearch(null);
    inputRef.current?.focus();
  }, [onChange]);

  const filteredSuggestions = excludeIds
    ? suggestions.filter((s) => !excludeIds.includes(s.collaborator.id))
    : suggestions;

  const shouldShowSuggestions = !debouncedQuery && filteredSuggestions.length > 0;
  const shouldShowSearch = debouncedQuery.length >= 2;

  const selectedHint = selectedFromSuggestions?.hint;

  return (
    <div>
      <Combobox
        as="div"
        value={selectedItem}
        onChange={handleSelectionChange}
        disabled={disabled}
        nullable
        by="id"
        className="mt-1"
      >
        <div className="relative">
          <Combobox.Input
            ref={inputRef}
            id={id}
            className={`input-primary w-full ${className || ''}`}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(item: CollaboratorPublic | null) => item?.name || ''}
            placeholder={placeholder ?? 'Selecione o gestor'}
            autoComplete="off"
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-10 flex items-center px-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Remover gestor selecionado"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          {(shouldShowSuggestions || shouldShowSearch) && (
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {shouldShowSuggestions && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-50">
                    {definitions.SUGGESTIONS_LABEL}
                  </div>

                  {filteredSuggestions.map((suggestion) => (
                    <Combobox.Option
                      key={suggestion.collaborator.id}
                      value={suggestion.collaborator}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ active, selected }) => (
                        <>
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <CollaboratorAvatar
                                photoUrl={suggestion.collaborator.photoUrl}
                                name={suggestion.collaborator.name}
                                size="xsmall"
                              />
                              <span
                                className={`block truncate ml-3 ${
                                  selected ? 'font-semibold' : ''
                                }`}
                              >
                                {suggestion.collaborator.name}
                              </span>
                            </div>
                            {suggestion.hint && (
                              <span
                                className={`text-xs ml-8 truncate ${
                                  active ? 'text-indigo-100' : 'text-gray-500'
                                }`}
                              >
                                {suggestion.hint}
                              </span>
                            )}
                          </div>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                active ? 'text-white' : 'text-indigo-600'
                              }`}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </>
              )}

              {shouldShowSearch && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-50">
                    {definitions.SEARCH_RESULTS_LABEL}
                  </div>

                  {searchLoading && (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      {definitions.SEARCHING}
                    </div>
                  )}

                  {searchError && (
                    <div className="relative cursor-default select-none py-2 px-4 text-red-700">
                      {definitions.SEARCH_ERROR}
                    </div>
                  )}

                  {!searchLoading && !searchError && searchResults.length === 0 && (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      {definitions.NO_RESULTS}
                    </div>
                  )}

                  {!searchLoading &&
                    !searchError &&
                    searchResults.map((collaborator) => {
                      const allocationManagerIds = new Set(allocationManagers.map((m) => m.id));
                      const isAllocationManager = allocationManagerIds.has(collaborator.id);
                      return (
                        <Combobox.Option
                          key={collaborator.id}
                          value={collaborator}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ active, selected }) => (
                            <>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <CollaboratorAvatar
                                    photoUrl={collaborator.photoUrl}
                                    name={collaborator.name}
                                    size="xsmall"
                                  />
                                  <span
                                    className={`block truncate ml-3 ${
                                      selected ? 'font-semibold' : ''
                                    }`}
                                  >
                                    {collaborator.name}
                                  </span>
                                </div>
                                {isAllocationManager && (
                                  <span
                                    className={`text-xs ml-2 px-2 py-1 rounded ${
                                      active
                                        ? 'bg-indigo-500 text-white'
                                        : 'bg-indigo-100 text-indigo-700'
                                    }`}
                                  >
                                    {definitions.ALLOCATION_MANAGER_BADGE}
                                  </span>
                                )}
                              </div>
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-indigo-600'
                                  }`}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      );
                    })}
                </>
              )}

            </Combobox.Options>
          )}
        </div>
      </Combobox>

      {selectedHint && (
        <p className="mt-1 text-xs text-gray-500 font-montserrat">
          {definitions.ALLOCATION_HINT_TEMPLATE.replace('{client}', selectedHint)}
        </p>
      )}

      {hasMultipleAllocationManagers && !selectedHint && (
        <p className="mt-1 text-xs text-amber-700 bg-amber-50 p-2 rounded font-montserrat">
          {definitions.MULTIPLE_MANAGERS_HINT}
        </p>
      )}
    </div>
  );
};

export default ManagerCombobox;
